<template>
  <div class="invoice-approvals">
    <div class="page-header">
      <h2 class="page-title">Утверждение цен накладных</h2>
    </div>

    <div class="filters">
      <q-select
        v-model="filters.approval_type"
        :options="typeOptions"
        emit-value
        map-options
        outlined
        dense
        label="Тип"
        clearable
        style="min-width: 180px"
        @update:model-value="loadData(1)"
      />

      <div class="filter-project">
        <div class="filter-input-wrapper" @click="openSbsProjectModal">
          <q-input
            :model-value="selectedSbsProjectLabel"
            outlined
            dense
            readonly
            placeholder="SBS-проект"
            style="min-width: 240px"
          >
            <template v-slot:prepend>
              <q-icon name="business" color="primary" />
            </template>
          </q-input>
        </div>
        <q-btn
          v-if="filters.sbs_project_id"
          flat round dense size="sm" icon="close" color="grey-7"
          @click="clearSbsProject"
        />
      </div>

      <div class="filter-date">
        <label>Период с:</label>
        <CustomDate
          :value="filters.date_from"
          placeholder="Дата"
          :width="130"
          @change_date="(v) => { filters.date_from = v; loadData(1); }"
        />
      </div>

      <div class="filter-date">
        <label>по:</label>
        <CustomDate
          :value="filters.date_to"
          placeholder="Дата"
          :width="130"
          @change_date="(v) => { filters.date_to = v; loadData(1); }"
        />
      </div>

      <q-btn flat dense icon="refresh" @click="loadData(page)">
        <q-tooltip>Обновить</q-tooltip>
      </q-btn>
    </div>

    <q-tabs
      v-model="filters.status"
      dense
      class="status-tabs"
      align="left"
      no-caps
      indicator-color="primary"
      active-color="primary"
      @update:model-value="loadData(1)"
    >
      <q-tab :name="'pending'"  label="На утверждении" />
      <q-tab :name="'approved'" label="Утверждённые" />
      <q-tab :name="'rejected'" label="Отклонённые" />
      <q-tab :name="null"       label="Все" />
    </q-tabs>

    <q-table
      :rows="rows"
      :columns="columns"
      :loading="loading"
      row-key="provider_request_invoice_approval_id"
      flat
      bordered
      :pagination="{ rowsPerPage: limit }"
      hide-pagination
      class="approvals-table"
    >
      <template v-slot:body-cell-status="props">
        <q-td :props="props">
          <q-badge :color="statusColor(props.row.status)" :label="statusLabel(props.row.status)" />
        </q-td>
      </template>

      <template v-slot:body-cell-approval_type="props">
        <q-td :props="props">
          <q-badge
            :color="props.row.approval_type === 'exceeded' ? 'orange' : 'blue'"
            :label="props.row.approval_type === 'exceeded' ? 'Превышение' : 'Обычное'"
          />
        </q-td>
      </template>

      <template v-slot:body-cell-invoice_sum="props">
        <q-td :props="props" class="text-right">
          {{ formatAmount(props.row.invoice_sum) }}
        </q-td>
      </template>

      <template v-slot:body-cell-decisions="props">
        <q-td :props="props">
          <div v-if="!Array.isArray(props.row.decisions_json) || props.row.decisions_json.length === 0" class="text-grey-6">—</div>
          <div v-else class="decisions-cell">
            <q-chip
              v-for="d in props.row.decisions_json"
              :key="d.provider_request_invoice_approval_decision_id"
              dense
              square
              :color="decisionColor(d.status)"
              :text-color="d.status === 'pending' ? 'grey-9' : 'white'"
              size="sm"
              class="decision-chip"
            >
              <span class="decision-chip__names">
                <span class="decision-chip__decided">{{ d.decide_employee_name || d.approver_employee_name || d.assign_type_name || 'Без роли' }}</span>
                <span
                  v-if="d.decide_employee_name && (d.approver_employee_name || d.assign_type_name) && d.decide_employee_name !== (d.approver_employee_name || d.assign_type_name)"
                  class="decision-chip__assigned"
                >{{ d.assign_type_name || d.approver_employee_name }}</span>
              </span>
              <q-icon
                v-if="d.status === 'approved'"
                name="check"
                size="14px"
                class="q-ml-xs"
              />
              <q-icon
                v-else-if="d.status === 'rejected'"
                name="close"
                size="14px"
                class="q-ml-xs"
              />
              <q-icon
                v-else
                name="schedule"
                size="14px"
                class="q-ml-xs"
              />
              <q-tooltip>
                <div v-if="d.approver_employee_name || d.assign_type_name">
                  Назначен: {{ d.approver_employee_name || d.assign_type_name }}
                </div>
                <div v-if="d.decide_employee_name">
                  {{ statusLabel(d.status) }}: {{ d.decide_employee_name }} ({{ d.decide_date }})
                </div>
                <div v-if="d.comment">Комментарий: {{ d.comment }}</div>
                <div v-if="!d.approver_employee_name && !d.assign_type_name && !d.decide_employee_name">Нет данных</div>
              </q-tooltip>
            </q-chip>
          </div>
        </q-td>
      </template>

      <template v-slot:body-cell-actions="props">
        <q-td :props="props" class="text-center">
          <q-btn flat dense icon="visibility" size="sm" @click="openDetails(props.row)">
            <q-tooltip>Позиции</q-tooltip>
          </q-btn>
          <template v-if="props.row.status === 'pending' && props.row.can_decide">
            <q-btn flat dense color="positive" icon="check_circle" size="sm" @click="openDecide(props.row, 'approved')">
              <q-tooltip>Утвердить</q-tooltip>
            </q-btn>
            <q-btn flat dense color="negative" icon="cancel" size="sm" @click="openDecide(props.row, 'rejected')">
              <q-tooltip>Отклонить</q-tooltip>
            </q-btn>
          </template>
          <q-icon
            v-else-if="props.row.status === 'pending' && !props.row.can_decide"
            name="lock"
            color="grey-6"
            size="18px"
            class="q-ml-sm"
          >
            <q-tooltip>Нет прав на утверждение этой накладной</q-tooltip>
          </q-icon>
        </q-td>
      </template>

      <template v-slot:no-data>
        <div class="full-width row flex-center q-pa-md text-grey-7">
          <q-icon name="inbox" size="32px" class="q-mr-sm" />
          <span>Нет записей</span>
        </div>
      </template>
    </q-table>

    <div v-if="totalPages > 1" class="pagination-row">
      <q-pagination
        v-model="page"
        :max="totalPages"
        :max-pages="7"
        boundary-numbers
        direction-links
        @update:model-value="loadData(page)"
      />
      <span class="q-ml-md text-grey-7">Всего: {{ totalCount }}</span>
    </div>

    <!-- Modal: Details -->
    <q-dialog v-model="detailsDialog">
      <q-card style="min-width: 720px; max-width: 92vw;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Позиции накладной №{{ detailRow?.invoice_num }}</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-card-section>
          <div class="details-meta">
            <div><b>Проект:</b> {{ detailRow?.project_name || '—' }}</div>
            <div><b>Поставщик:</b> {{ detailRow?.provider_name || '—' }}</div>
            <div><b>Запросил:</b> {{ detailRow?.request_employee_name }} ({{ detailRow?.request_date }})</div>
            <div v-if="detailRow?.decide_date">
              <b>Решение:</b> {{ statusLabel(detailRow?.status) }} — {{ detailRow?.decide_employee_name }} ({{ detailRow?.decide_date }})
            </div>
            <div v-if="detailRow?.comment"><b>Комментарий:</b> {{ detailRow.comment }}</div>
          </div>

          <q-table
            v-if="approvalItems(detailRow).length"
            :rows="approvalItems(detailRow)"
            :columns="itemColumns"
            row-key="provider_request_item_id"
            flat
            dense
            hide-bottom
            :pagination="{ rowsPerPage: 0 }"
          >
            <template v-slot:body-cell-exceeded="props">
              <q-td :props="props" class="text-center">
                <q-icon v-if="props.row.exceeded" name="warning" color="orange" size="18px">
                  <q-tooltip>Цена выше индикативной</q-tooltip>
                </q-icon>
                <q-icon v-else name="check_circle" color="green" size="18px" />
              </q-td>
            </template>
            <template v-slot:body-cell-entered_price="props">
              <q-td :props="props" class="text-right">{{ formatAmount(props.row.entered_price) }}</q-td>
            </template>
            <template v-slot:body-cell-indicative_price="props">
              <q-td :props="props" class="text-right">{{ formatAmount(props.row.indicative_price) }}</q-td>
            </template>
          </q-table>

          <template v-if="noIndicativeItems(detailRow).length">
            <div class="no-indicative-header">
              <q-icon name="info" size="14px" color="grey-6" class="q-mr-xs" />
              Без индикативной цены — утверждение не требуется
            </div>
            <q-table
              :rows="noIndicativeItems(detailRow)"
              :columns="itemColumnsNoIndicative"
              row-key="provider_request_item_id"
              flat
              dense
              hide-bottom
              :pagination="{ rowsPerPage: 0 }"
              class="no-indicative-table"
            >
              <template v-slot:body-cell-entered_price="props">
                <q-td :props="props" class="text-right">{{ formatAmount(props.row.entered_price) }}</q-td>
              </template>
            </q-table>
          </template>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Modal: SBS-project select -->
    <q-dialog v-model="sbsProjectDialog.show" persistent>
      <q-card style="min-width: 700px; max-width: 900px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Выбор SBS-проекта</div>
          <q-space />
          <q-btn icon="close" flat round dense @click="sbsProjectDialog.show = false" />
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="sbsProjectSearch"
            outlined dense clearable autofocus
            placeholder="Поиск по названию или коду..."
            @update:model-value="onSbsSearchInput"
          >
            <template v-slot:prepend><q-icon name="search" /></template>
          </q-input>
          <div style="margin-top: 16px;">
            <q-linear-progress v-if="isLoadingSbsProjects" indeterminate />
            <div v-else-if="sbsProjectList.length === 0" style="text-align: center; padding: 40px; color: #999;">
              <q-icon name="folder_off" size="48px" />
              <div style="margin-top: 12px;">Проекты не найдены</div>
            </div>
            <template v-else>
              <q-table
                :rows="sbsProjectList"
                :columns="[
                  { name: 'sbs_project_id', label: 'ID', field: 'sbs_project_id', align: 'center', style: 'width: 80px' },
                  { name: 'naimenovanie', label: 'Наименование', field: 'naimenovanie', align: 'left' },
                  { name: 'kod', label: 'Код', field: 'kod', align: 'left', style: 'width: 120px' },
                  { name: 'actions', label: '', field: 'actions', align: 'center', style: 'width: 100px' },
                ]"
                row-key="sbs_project_id"
                flat bordered hide-bottom
                :rows-per-page-options="[0]"
                style="max-height: 400px; overflow-y: auto;"
              >
                <template v-slot:body-cell-actions="props">
                  <q-td :props="props">
                    <q-btn label="Выбрать" color="primary" size="sm" unelevated @click="selectSbsProject(props.row)" />
                  </q-td>
                </template>
              </q-table>
              <div style="font-size: 13px; color: #666; margin-top: 8px; padding: 0 4px;">
                Показано {{ sbsProjectList.length }}
                <span v-if="sbsProjectTotalCount > sbsProjectList.length"> из {{ sbsProjectTotalCount }} — уточните поиск</span>
              </div>
            </template>
          </div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="sbsProjectDialog.show = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Modal: Decide -->
    <q-dialog v-model="decideDialog" persistent>
      <q-card style="min-width: 460px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            {{ decideAction === 'approved' ? 'Утвердить' : 'Отклонить' }} накладную №{{ decideRow?.invoice_num }}
          </div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup :disable="deciding" />
        </q-card-section>
        <q-card-section>
          <q-input
            v-model="decideComment"
            type="textarea"
            outlined
            dense
            autogrow
            label="Комментарий"
            :placeholder="decideAction === 'rejected' ? 'Укажите причину отклонения' : 'Необязательно'"
          />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" v-close-popup :disable="deciding" />
          <q-btn
            unelevated
            :color="decideAction === 'approved' ? 'positive' : 'negative'"
            :label="decideAction === 'approved' ? 'Утвердить' : 'Отклонить'"
            :loading="deciding"
            @click="submitDecide"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { supplierRequestsAPI } from "@/views/SupplierRequests/services/api";
import { getSbsProjects } from "@/views/SupplierRequests/services";
import CustomDate from "@/components/form/CustomDate";

export default {
  name: "InvoiceApprovals",
  components: { CustomDate },
  data() {
    return {
      loading: false,
      rows: [],
      totalCount: 0,
      page: 1,
      limit: 25,

      filters: {
        status: "pending",
        approval_type: null,
        sbs_project_id: null,
        date_from: null,
        date_to: null,
      },

      sbsProjectDialog: { show: false },
      sbsProjectList: [],
      sbsProjectSearch: "",
      sbsProjectPage: 1,
      sbsProjectLimit: 50,
      sbsProjectTotalCount: 0,
      sbsSearchDebounce: null,
      isLoadingSbsProjects: false,
      selectedSbsProject: null,

      typeOptions: [
        { value: "normal",   label: "Обычное" },
        { value: "exceeded", label: "Превышение" },
      ],

      columns: [
        { name: "request_date",   label: "Дата запроса", field: "request_date",   align: "left" },
        { name: "invoice_num",    label: "Накладная",    field: row => `№${row.invoice_num} от ${row.invoice_date} (Заявка #${row.provider_request_id})`, align: "left" },
        { name: "project_name",   label: "Проект",       field: "project_name",   align: "left" },
        { name: "provider_name",  label: "Поставщик",    field: "provider_name",  align: "left" },
        { name: "approval_type",  label: "Тип",          field: "approval_type",  align: "center" },
        { name: "invoice_sum",    label: "Сумма",        field: "invoice_sum",    align: "right" },
        { name: "request_employee_name", label: "Запросил", field: "request_employee_name", align: "left" },
        { name: "decisions",      label: "Решения",      field: "decisions_json", align: "left" },
        { name: "status",         label: "Статус",       field: "status",         align: "center" },
        { name: "actions",        label: "Действия",     field: () => "",         align: "center" },
      ],

      itemColumns: [
        { name: "exceeded",             label: "",                field: "exceeded",            align: "center", style: "width: 40px" },
        { name: "material_name",        label: "Материал",        field: "material_name",       align: "left" },
        { name: "material_cnt",         label: "Кол-во",          field: "material_cnt",        align: "center", style: "width: 90px" },
        { name: "material_unit_name",   label: "Ед. изм.",        field: "material_unit_name",  align: "center", style: "width: 80px" },
        { name: "entered_price",        label: "Цена поставщика", field: "entered_price",       align: "right",  style: "width: 160px" },
        { name: "indicative_price",     label: "Индикативная",    field: "indicative_price",    align: "right",  style: "width: 160px" },
      ],
      itemColumnsNoIndicative: [
        { name: "material_name",       label: "Материал", field: "material_name",      align: "left" },
        { name: "material_cnt",        label: "Кол-во",   field: "material_cnt",       align: "center", style: "width: 90px" },
        { name: "material_unit_name",  label: "Ед. изм.", field: "material_unit_name", align: "center", style: "width: 80px" },
        { name: "entered_price",       label: "Цена",     field: "entered_price",      align: "right",  style: "width: 160px" },
      ],

      detailsDialog: false,
      detailRow: null,

      decideDialog: false,
      decideRow: null,
      decideAction: "approved",
      decideComment: "",
      deciding: false,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil((this.totalCount || 0) / this.limit) || 1;
    },
    selectedSbsProjectLabel() {
      return this.selectedSbsProject?.naimenovanie || "";
    },
  },
  mounted() {
    this.loadData(1);
  },
  methods: {
    approvalItems(row) {
      return (row?.items_json || []).filter(i => i.indicative_price > 0);
    },
    noIndicativeItems(row) {
      return (row?.items_json || []).filter(i => !(i.indicative_price > 0));
    },
    formatAmount(v) {
      if (v == null) return "—";
      return Number(v).toLocaleString("ru-RU", { maximumFractionDigits: 2 }) + " ₸";
    },
    statusColor(s) {
      if (s === "approved") return "positive";
      if (s === "rejected") return "negative";
      return "amber-9";
    },
    statusLabel(s) {
      if (s === "approved") return "Утверждено";
      if (s === "rejected") return "Отклонено";
      return "На утверждении";
    },
    decisionColor(s) {
      if (s === "approved") return "positive";
      if (s === "rejected") return "negative";
      return "amber-3";
    },
    async openSbsProjectModal() {
      this.sbsProjectDialog.show = true;
      this.sbsProjectSearch = "";
      this.sbsProjectPage = 1;
      this.sbsProjectTotalCount = 0;
      this.sbsProjectList = [];
      await this.loadSbsProjects();
    },
    async loadSbsProjects(page) {
      if (page) this.sbsProjectPage = page;
      this.isLoadingSbsProjects = true;
      try {
        const data = await getSbsProjects(this.sbsProjectSearch || "", this.sbsProjectPage, this.sbsProjectLimit);
        if (Array.isArray(data) && data.length > 0) {
          this.sbsProjectList = data;
          this.sbsProjectTotalCount = data[0].total_count || data.length;
        } else {
          this.sbsProjectList = [];
          this.sbsProjectTotalCount = 0;
        }
      } finally {
        this.isLoadingSbsProjects = false;
      }
    },
    onSbsSearchInput() {
      if (this.sbsSearchDebounce) clearTimeout(this.sbsSearchDebounce);
      this.sbsSearchDebounce = setTimeout(() => {
        this.sbsProjectPage = 1;
        this.loadSbsProjects();
      }, 400);
    },
    selectSbsProject(row) {
      this.filters.sbs_project_id = row.sbs_project_id;
      this.selectedSbsProject = row;
      this.sbsProjectDialog.show = false;
      this.loadData(1);
    },
    clearSbsProject() {
      this.filters.sbs_project_id = null;
      this.selectedSbsProject = null;
      this.loadData(1);
    },
    async loadData(page) {
      this.page = page || 1;
      this.loading = true;
      try {
        const res = await supplierRequestsAPI.getInvoiceApprovals({
          status: this.filters.status || undefined,
          approval_type: this.filters.approval_type || undefined,
          sbs_project_id: this.filters.sbs_project_id || undefined,
          date_from: this.filters.date_from || undefined,
          date_to: this.filters.date_to || undefined,
          page: this.page,
          limit: this.limit,
        });
        this.rows = res?.data || [];
        this.totalCount = parseInt(res?.total_count) || 0;
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.message || "Ошибка загрузки" });
      } finally {
        this.loading = false;
      }
    },
    openDetails(row) {
      this.detailRow = row;
      this.detailsDialog = true;
    },
    openDecide(row, action) {
      this.decideRow = row;
      this.decideAction = action;
      this.decideComment = "";
      this.decideDialog = true;
    },
    async submitDecide() {
      this.deciding = true;
      try {
        await supplierRequestsAPI.decideInvoiceApproval(
          this.decideRow.provider_request_invoice_approval_id,
          this.decideAction,
          this.decideComment || null
        );
        this.$q.notify({ type: "positive", message: this.decideAction === "approved" ? "Утверждено" : "Отклонено" });
        this.decideDialog = false;
        await this.loadData(this.page);
      } catch (e) {
        this.$q.notify({ type: "negative", message: e?.message || "Ошибка" });
      } finally {
        this.deciding = false;
      }
    },
  },
};
</script>

<style scoped>
.invoice-approvals {
  padding: 16px 20px;
}
.page-header {
  margin-bottom: 16px;
}
.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
  padding: 12px;
  background: #f7f9fc;
  border: 1px solid #e0e6ef;
  border-radius: 8px;
}
.filter-date,
.filter-project {
  display: flex;
  align-items: center;
  gap: 6px;
}
.filter-date label {
  font-size: 12px;
  color: #626262;
}
.filter-input-wrapper {
  cursor: pointer;
}
.filter-input-wrapper :deep(.q-field__control) {
  cursor: pointer;
}
.status-tabs {
  margin-bottom: 8px;
  border-bottom: 1px solid #e0e6ef;
}
.approvals-table {
  background: #fff;
}
.details-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 16px;
  padding: 8px 0 16px;
  font-size: 13px;
  color: #3a3a3a;
}
.no-indicative-header {
  display: flex;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 4px;
  font-size: 11px;
  color: #9e9e9e;
  font-style: italic;
}
.no-indicative-table {
  opacity: 0.7;
}
.pagination-row {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
}
.decisions-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.decision-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 6px;
  height: auto !important;
  min-height: 24px;
}
.decision-chip :deep(.q-chip__content) {
  align-items: center;
}
.decision-chip__names {
  display: flex;
  flex-direction: column;
  max-width: 180px;
  line-height: 1.3;
  padding: 2px 0;
}
.decision-chip__decided {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
}
.decision-chip__assigned {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 10px;
  font-weight: 400;
  opacity: 0.75;
}
</style>
